import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import {
  LayoutDashboard, Phone, BarChart3, Settings, Building2, LogOut,
  ChevronLeft, ChevronRight, Users, DollarSign, Shield, Menu, X,
  Wrench, CreditCard, Mic, Webhook, Star, Target
} from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher';
import AdminNotificationBell from './AdminNotificationBell';
import api from '../api';

export default function Sidebar({ onCollapsedChange }) {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [buildQueueCount, setBuildQueueCount] = useState(0);
  const [callsToday, setCallsToday] = useState(null);
  const isAdmin = user?.role === 'admin';
  // Treat legacy 'client' role as manager-equivalent for client-facing pages
  // (billing + agent config). The newer admin invite flow uses 'manager' / 'user'.
  const isManager = user?.role === 'manager' || user?.role === 'client';

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Notify Layout of collapse state changes (for main margin)
  useEffect(() => {
    if (onCollapsedChange) onCollapsedChange(collapsed);
  }, [collapsed, onCollapsedChange]);

  // Prevent body scroll when mobile drawer is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  // Fetch calls today count
  useEffect(() => {
    if (!user) return;
    const today = new Date().toISOString().slice(0, 10);
    api.get(`/api/calls?date_from=${today}T00:00:00&date_to=${today}T23:59:59&limit=1`)
      .then(res => setCallsToday(res.data?.total ?? 0))
      .catch(() => setCallsToday(null));
  }, [user]);

  // Fetch pending build queue count for admin badge
  useEffect(() => {
    if (!isAdmin) return;
    api.get('/api/admin/build-queue')
      .then(res => {
        const tasks = res.data.tasks || res.data || [];
        const pending = Array.isArray(tasks) ? tasks.filter(t => t.status !== 'complete' && t.status !== 'completed' && t.status !== 'done').length : 0;
        setBuildQueueCount(pending);
      })
      .catch(() => setBuildQueueCount(0));
  }, [isAdmin]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Client sidebar — AI-agent + Fakturering are MANAGER ONLY (hide for role 'user')
  const clientLinks = [
    { to: '/dashboard', icon: LayoutDashboard, label: t('nav_overview') },
    { to: '/calls', icon: Phone, label: t('nav_calls') },
    { to: '/contacts', icon: Users, label: t('nav_contacts') },
    { to: '/leads', icon: Target, label: 'Leads' },
    { to: '/analytics', icon: BarChart3, label: t('nav_analytics') },
    { to: '/quality', icon: Star, label: 'Kvalitet' },
    ...(isManager ? [
      { to: '/agent-config', icon: Mic, label: 'AI-agent' },
      { to: '/billing', icon: CreditCard, label: 'Fakturering' },
    ] : []),
    { to: '/settings', icon: Settings, label: t('nav_settings') },
  ];

  // Admin sidebar — admin-only items, no client pages
  const adminLinks = [
    { to: '/admin', icon: LayoutDashboard, label: t('nav_overview') },
    { to: '/admin/companies', icon: Building2, label: t('nav_companies') },
    { to: '/admin/users', icon: Users, label: t('nav_users') },
    { to: '/admin/calls', icon: Phone, label: t('nav_calls') },
    { to: '/admin/finance', icon: DollarSign, label: t('nav_finance') },
    { to: '/build-queue', icon: Wrench, label: 'Build Queue', badge: buildQueueCount },
    { to: '/admin/integrations', icon: Webhook, label: 'Integrasjoner' },
    { to: '/admin/settings', icon: Settings, label: t('nav_settings') },
  ];

  const links = isAdmin ? adminLinks : clientLinks;

  const sidebarContent = (isMobile = false) => (
    <aside
      data-testid="sidebar"
      className={`
        flex flex-col h-full bg-navy-900 border-r border-navy-600
        ${isMobile ? 'w-72' : `transition-all duration-300 ${collapsed ? 'w-16' : 'w-60'}`}
      `}
    >
      {/* Header */}
      <div className={`h-16 flex items-center border-b border-navy-600 flex-shrink-0 ${(isMobile || !collapsed) ? 'px-4 gap-3' : 'justify-center px-2'}`}>
        <img src="/logo.png" alt="logo" className="w-8 h-8 rounded-lg object-cover flex-shrink-0" />
        {(isMobile || !collapsed) && user?.company_name && (
          <span className="font-heading text-base font-bold text-white tracking-tight truncate flex-1">{user.company_name}</span>
        )}
        {isMobile && (
          <button onClick={() => setMobileOpen(false)} className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-navy-800 transition-colors ml-auto flex-shrink-0">
            <X size={18} />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 py-4 overflow-y-auto">
        {isAdmin && (isMobile || !collapsed) && (
          <div className="px-5 mb-2 text-[10px] font-semibold tracking-[0.15em] uppercase text-slate-500">Administrasjon</div>
        )}
        <div className={`${(isMobile || !collapsed) ? 'px-3' : 'px-2'} space-y-0.5`}>
          {links.map(({ to, icon: Icon, label, badge }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/admin'}
              data-testid={`nav-${to.replace(/\//g, '-').replace(/^-/, '')}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  isActive ? 'bg-navy-700 text-white' : 'text-slate-400 hover:bg-navy-800 hover:text-white'
                } ${(!isMobile && collapsed) ? 'justify-center' : ''}`
              }
            >
              <Icon size={18} className="flex-shrink-0" />
              {(isMobile || !collapsed) && <span className="flex-1">{label}</span>}
              {(isMobile || !collapsed) && badge > 0 && (
                <span className="text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded-full bg-accent-yellow/20 text-accent-yellow">{badge}</span>
              )}
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Today's stats widget */}
      {(isMobile || !collapsed) && callsToday !== null && (
        <div className="px-4 py-2 border-t border-navy-600/50">
          <div className="bg-navy-700/50 rounded-lg px-3 py-2 flex items-center gap-2">
            <span className="text-base">📞</span>
            <div>
              <span className="text-sm font-semibold text-white">{callsToday}</span>
              <span className="text-xs text-slate-500 ml-1">samtaler i dag</span>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="border-t border-navy-600 flex-shrink-0">
        {(isMobile || !collapsed) ? (
          <div className="px-4 py-3 flex items-center gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${isAdmin ? 'bg-accent-yellow/20 text-accent-yellow' : 'bg-accent-blue/20 text-accent-blue'}`}>
              {isAdmin ? <Shield size={14} /> : (user?.name || '?').split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-white truncate">{user?.name}</div>
              <div className="text-xs text-slate-500 truncate">{user?.email}</div>
              {isAdmin && <div className="text-[10px] text-accent-blue mt-0.5 uppercase tracking-wider">Administrator</div>}
            </div>
          </div>
        ) : (
          <div className="flex justify-center py-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${isAdmin ? 'bg-accent-yellow/20 text-accent-yellow' : 'bg-accent-blue/20 text-accent-blue'}`}>
              {isAdmin ? <Shield size={14} /> : (user?.name || '?').charAt(0).toUpperCase()}
            </div>
          </div>
        )}
        <div className={`flex ${(!isMobile && collapsed) ? 'flex-col items-center gap-4 py-4' : 'items-center justify-between px-3 pb-3'}`}>
          <div className="flex items-center gap-1">
            <button
              data-testid="logout-button"
              onClick={handleLogout}
              className="flex items-center gap-2 px-3 py-2 text-sm text-slate-400 hover:text-accent-red rounded-lg hover:bg-navy-800 transition-colors"
            >
              <LogOut size={16} />
              {(isMobile || !collapsed) && <span>{t('nav_logout')}</span>}
            </button>
            {(isMobile || !collapsed) && <LanguageSwitcher />}
            {isAdmin && (isMobile || !collapsed) && <AdminNotificationBell />}
          </div>
          {!isMobile && (
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="p-2 text-slate-500 hover:text-white rounded-lg hover:bg-navy-800 transition-colors"
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
            </button>
          )}
        </div>
      </div>
    </aside>
  );

  return (
    <>
      {/* Mobile hamburger button — only visible on mobile */}
      <button
        onClick={() => setMobileOpen(true)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-navy-800 border border-navy-600 text-slate-300 hover:text-white rounded-lg shadow-lg transition-colors"
        aria-label="Open menu"
      >
        <Menu size={20} />
      </button>

      {/* Mobile backdrop overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile drawer — slides in from left */}
      <div className={`lg:hidden fixed top-0 left-0 h-full z-50 transition-transform duration-300 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        {sidebarContent(true)}
      </div>

      {/* Desktop sidebar — always visible on lg+ */}
      <div className="hidden lg:block fixed top-0 left-0 h-screen z-30">
        {sidebarContent(false)}
      </div>
    </>
  );
}

Sidebar.propTypes = {
  onCollapsedChange: PropTypes.func,
};
